// Six morphologies cibles pour le swimwear.
// Référentiel utilisé par le moteur bayésien, la jauge et la recommandation.

export type Morphology = 'A' | 'V' | 'H' | 'X' | '8' | 'O';

export const MORPHOLOGIES: readonly Morphology[] = ['A', 'V', 'H', 'X', '8', 'O'] as const;

export const MORPHOLOGY_LABELS: Record<Morphology, string> = {
  A: 'Pyramide (A) — hanches plus marquées que les épaules',
  V: 'Pyramide inversée (V) — épaules plus marquées que les hanches',
  H: 'Rectangle (H) — silhouette droite, taille peu marquée',
  X: 'Sablier (X) — épaules et hanches alignées, taille marquée',
  '8': 'Sablier généreux (8) — buste et hanches volumineux, taille très creusée',
  O: 'Ronde (O) — courbes généreuses, taille peu dessinée',
};

export const MORPHOLOGY_DESCRIPTIONS: Record<Morphology, string> = {
  A: "Tes hanches sont plus larges que tes épaules, et ta taille est marquée. On met l'accent sur le haut pour rééquilibrer.",
  V: 'Tes épaules sont plus larges que tes hanches. On apporte du volume sur le bas pour rééquilibrer la silhouette.',
  H: 'Ta silhouette est plutôt athlétique, taille peu marquée. On crée des courbes avec des coupes structurantes.',
  X: 'Épaules et hanches alignées, taille fine. La plupart des coupes te vont — on souligne la taille.',
  '8': 'Buste et hanches généreux avec une taille très creusée. On privilégie le maintien et les coupes qui galbent sans comprimer.',
  O: 'Des formes pleines au niveau du buste et de la taille. On allonge la silhouette avec des coupes fluides.',
};

/** Identifiant unique d'une question dans la banque. */
export type QuestionId = string;

/** Identifiant unique d'une option de réponse. */
export type OptionId = string;

/**
 * Vraisemblance P(réponse | morphologie) pour chaque option d'une question.
 * On stocke des poids relatifs (pas besoin de normaliser, le moteur s'en charge).
 * Un poids de 1 = neutre, >1 = compatible avec cette morphologie, <1 = incompatible.
 */
export type LikelihoodVector = Record<Morphology, number>;

/**
 * Points additifs par morphologie (système v3 du schéma Jolie Fille).
 * Ex: { V: +40, X: 0, ... } = répondre cette option ajoute 40 points à V.
 * Les points peuvent être négatifs (ex: ventre proéminent → X-20).
 */
export type PointsVector = Partial<Record<Morphology, number>>;

/** Mode d'intégration du formulaire selon où il est placé. */
export type FormMode = 'home' | 'product';

export interface QuestionOption {
  id: OptionId;
  label: string;
  /** Texte court affiché sur le bouton (si différent du label). */
  buttonLabel?: string;
  /** Vraisemblances P(option | morphologie). */
  likelihood: LikelihoodVector;
  /** Points additifs par morphologie (système v3 — additif au lieu de multiplicatif). */
  points?: PointsVector;
  /**
   * Mots-clés (lowercase, racines) qui matchent cette option pour la
   * classification de texte libre. Plus la liste est longue et spécifique,
   * meilleure est la classification offline.
   */
  keywords?: string[];
  /** Illustration : clé silhouette ('A'..'O'|'unknown') ou URL d'image (mode formulaire). */
  image?: string;
  /** Sous-titre affiché sous le titre dans une carte (mode formulaire). */
  description?: string;
}

export type QuestionKind =
  | 'measurement' // saisie numérique (cm) — alimente le classifieur par ratios
  | 'confirmation' // question visuelle Oui/Non qui affine via Bayes
  | 'morphology' // (legacy) question subjective bayésienne
  | 'sizing'
  | 'preference';

/** Champ de mesure corporelle pour les questions de type 'measurement'. */
export type MeasurementField = 'bust' | 'waist' | 'hips' | 'shoulders' | 'height';

export interface MeasurementSpec {
  field: MeasurementField;
  unit: 'cm';
  min: number;
  max: number;
  placeholder?: string;
}

export interface Question {
  id: QuestionId;
  kind: QuestionKind;
  /** Texte prononcé par Eteka. */
  prompt: string;
  /** Options proposées (vide pour les questions 'measurement'). */
  options: QuestionOption[];
  /** Métadonnée de saisie numérique (questions 'measurement' uniquement). */
  measurement?: MeasurementSpec;
  /**
   * Si défini, la question n'est posée que si cette condition est vraie.
   * Permet la logique adaptative (ex: confirmation seulement si ambigu).
   */
  showIf?: (ctx: ConditionContext) => boolean;
  /** Servie en priorité, dans l'ordre du bank (mesures + sizing obligatoire). */
  mandatory?: boolean;
  /** Sélection multiple autorisée (mode formulaire, ex: préférence de style). */
  multiSelect?: boolean;
  /** Titre court de l'étape (mode formulaire). */
  title?: string;
}

export interface ConditionContext {
  answers: Record<QuestionId, OptionId>;
  topMorphology: Morphology;
  confidence: number;
  /** Distribution complète, pour détecter les morphologies en compétition. */
  probabilities: Record<Morphology, number>;
  /** Mode d'intégration : home (avec Q1 type) ou product (sans Q1, sans reco produit). */
  mode: FormMode;
  /** En mode product, l'ID Eteka du produit courant — utilisé par showIf pour
   *  ne poser QUE les questions nécessaires au sizing de ce produit. */
  currentProductId?: string;
}

/** État du moteur après chaque réponse. */
export interface EngineState {
  /** Distribution de probabilité P(morphologie | réponses). */
  probabilities: Record<Morphology, number>;
  /** Points cumulés par morphologie (système v3 — additif). */
  morphoPoints: Record<Morphology, number>;
  /** Réponses données jusqu'ici. */
  answers: Record<QuestionId, OptionId>;
  /** Liste des question IDs déjà posées (ordre conservé). */
  asked: QuestionId[];
  /** Morphologie la plus probable + sa confiance. */
  top: { morphology: Morphology; confidence: number };
  /** Vrai si on a atteint le seuil de confiance ou la limite de questions. */
  done: boolean;
}
